import React, { useState, useRef, useEffect } from "react";
import { Select } from "antd";
import { withTranslation } from "react-i18next";
import { CalendarOutlined } from "@ant-design/icons";
import { RangePicker } from "@tailormed/common-client/component/DatePicker";

import { SingleSelect } from "./CustomSelect";
import { dateFilterTypes } from "../constant/filters";
import { DATE_FORMAT, setDateRange } from "../constant/date";
import moment from "moment-timezone";

const { Option } = Select;

const DateRangeFilterComponent = ({
  t,
  timeRange,
  disabled,
  onSelect,
  id,
  allowClear,
  onClear,
  defaultValue,
  placeHolder,
  bordered = false,
  withRangePicker = true
}) => {
  const fromDate = timeRange.fromDate ? moment(timeRange.fromDate, DATE_FORMAT) : null;
  const toDate = timeRange.toDate ? moment(timeRange.toDate, DATE_FORMAT) : null;
  const selectedValue = timeRange.value;
  const isRangeSelected = fromDate && toDate && !selectedValue;
  const [isRangeOpen, setIsRangeOpen] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dates, setDates] = useState({ fromDate: null, toDate: null });

  useEffect(() => {
    if (!fromDate && !toDate) {
      setDates({ fromDate: null, toDate: null });
    }
  }, [fromDate, toDate]);

  const updateDropdownPosition = (scrollingEnabled = false) => {
    const dropdown = dropdownRef.current;
    const container = containerRef.current;

    if (dropdown && container) {
      const containerRect = container.getBoundingClientRect();

      let adjustmentOffsetBottom = 0;

      const dropdownListElement = document.querySelector(".ant-select-dropdown:not(.ant-select-dropdown-hidden)");

      if (dropdownListElement) {
        const collapseRect = dropdownListElement.getBoundingClientRect();
        adjustmentOffsetBottom = -(adjustmentOffsetBottom + collapseRect.height);
      }

      dropdown.style.top = `${containerRect.bottom + adjustmentOffsetBottom}px`;

      const arrow = dropdown.querySelector(".ant-picker-range-arrow");
      if (arrow && scrollingEnabled) {
        arrow.style.top = "unset";
        arrow.style.transform = "rotate(45deg)";
        arrow.style.bottom = "2.5px";
      }
    }
  };

  const handleOpenChange = (open) => {
    setIsRangeOpen(open);
    if (open) {
      setTimeout(updateDropdownPosition, 0);
    } else {
      setOpen(false);
    }
  };

  const setRangePicker = (values) => {
    setOpen(false);
    const formattedDates = setDateRange(values[0], values[1]);
    onSelect(...formattedDates);
  };

  const setDatesPick = (val) => {
    const selectedDates = {};
    selectedDates.fromDate = val[0] ?? null;
    selectedDates.toDate = val[1] ?? null;

    setDates(selectedDates);
  };

  const disabledDate = (current) => {
    if (!current || !dates || (!dates.fromDate && !dates.toDate)) {
      return false;
    }

    if (dates.fromDate) {
      const maxDate = moment(dates.fromDate).add(2, "years");
      if (current.isAfter(maxDate, "day")) {
        return true;
      }
    }

    if (dates.toDate) {
      const minDate = moment(dates.toDate).subtract(2, "years");
      if (current.isBefore(minDate, "day")) {
        return true;
      }
    }

    return false;
  };

  return (
    <div id={id ? id : undefined} ref={containerRef}>
      <SingleSelect
        showSearch={false}
        placeholder={placeHolder}
        disabled={disabled}
        open={open}
        bordered={bordered}
        onClick={() => {
          if (isFocus && !open) setOpen(true);
        }}
        onFocus={() => {
          setIsFocus(true);
          setOpen(true);
        }}
        onBlur={() => {
          setIsFocus(false);
          if (!isRangeOpen) setOpen(false);
        }}
        onSelect={(value) => {
          setOpen(false);

          if (value === selectedValue) {
            return;
          } else {
            if (value === defaultValue) {
              onClear();
            } else {
              onSelect(null, null, value);
            }
          }
        }}
        suffixIcon={<CalendarOutlined />}
        value={
          //: value of null will make the placeHolder take effect.
          isRangeSelected
            ? `${fromDate.format(DATE_FORMAT)} - ${toDate.format(DATE_FORMAT)}`
            : selectedValue === defaultValue
            ? null
            : selectedValue
        }
        dropdownRender={(menu) => (
          <div>
            {menu}
            {withRangePicker && (
              <div id={`${id}_range_picker`}>
                <RangePicker
                  value={isRangeSelected ? [fromDate, toDate] : null}
                  disabledDate={disabledDate}
                  suffixIcon={null}
                  clearIcon={null}
                  separator={null}
                  bordered={false}
                  allowEmpty={[false, false]}
                  onChange={setRangePicker}
                  onOpenChange={handleOpenChange}
                  onCalendarChange={setDatesPick}
                  placement="bottom"
                />
              </div>
            )}
          </div>
        )}
        allowClear={allowClear}
        onClear={onClear}
      >
        {dateFilterTypes.map((filterType) => (
          <Option value={filterType.value} key={filterType.value} id={`date-range-filter-option-${filterType.value}`}>
            {t(filterType.value)}
          </Option>
        ))}
      </SingleSelect>
    </div>
  );
};

const DateRangeFilter = withTranslation()(DateRangeFilterComponent);

export { DateRangeFilter };
